/*
  Quick view renders a whole section server-side, and that round trip is the
  entire perceived wait: the popup opens onto an empty dialog and sits there
  until it lands. Two things fix that — the response is cached for the page
  session (so reopening anything is instant), and the request is warmed from
  hover/touch/focus, which is well before the click on desktop and a head start
  everywhere else.
*/
const QuickViewCache = {
  html: new Map(),
  pending: new Map(),
  hoverTimer: null,

  // The product URL always carries a ?variant=, so &view= is the right joiner.
  sectionUrl(productUrl) {
    return `${productUrl}&view=quick-view`;
  },

  fetch(productUrl) {
    const url = this.sectionUrl(productUrl);
    if (this.html.has(url)) return Promise.resolve(this.html.get(url));
    if (this.pending.has(url)) return this.pending.get(url);

    const request = fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const content = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.quick-view__content');

        if (!content) throw new Error('quick-view returned no content');

        this.html.set(url, content.innerHTML);
        return content.innerHTML;
      })
      .finally(() => this.pending.delete(url));

    this.pending.set(url, request);
    return request;
  },

  /*
    Speculative, so failures stay silent — the real fetch on click will surface
    the error. `delay` throttles hover: a pointer crossing a product grid would
    otherwise fire a request per card it passes over.
  */
  warm(productUrl, delay) {
    if (!productUrl) return;

    clearTimeout(this.hoverTimer);

    if (!delay) {
      this.fetch(productUrl).catch(() => {});
      return;
    }

    this.hoverTimer = setTimeout(() => {
      this.fetch(productUrl).catch(() => {});
    }, delay);
  },

  cancelWarm() {
    clearTimeout(this.hoverTimer);
  }
};

class QuickViewDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('close', () => {
        const drawerContent = this.querySelector('.quick-view__content');
        drawerContent.innerHTML = '';
        drawerContent.classList.remove('hide-cover');
  
        document.dispatchEvent(new CustomEvent('quickview:close'));
      });
    }
  }
  customElements.define('quick-view-drawer', QuickViewDrawer);
  
  class QuickViewButton extends HTMLElement {
    constructor() {
      super();

      this.quickButton = this.querySelector('.quick') || this.closest('.quick');
      this.cardExtras = this.quickButton?.closest('.card__extras');
    }

    connectedCallback() {
      /*
        pointerover rather than pointerenter: it bubbles, so it still reaches
        this element when it is laid out as `display: contents` (the flavor
        picker wraps its info button that way).
      */
      this._onWarmHover = () => QuickViewCache.warm(this.dataset.productVariantUrl, 100);
      this._onWarmIntent = () => QuickViewCache.warm(this.dataset.productVariantUrl);
      this._onWarmCancel = () => QuickViewCache.cancelWarm();

      this.addEventListener('pointerover', this._onWarmHover);
      this.addEventListener('pointerout', this._onWarmCancel);
      this.addEventListener('pointerdown', this._onWarmIntent);
      this.addEventListener('focusin', this._onWarmIntent);

      this._onClick = (event) => {
        /*
          .card-quick-view is only on the card <a> when quick view or quick add
          is enabled; .card__actions can render regardless of both, so it needs
          its own clause or the card link fires alongside the quick view.
        */
        if (
          event.target.closest('.card-quick-view') ||
          event.target.closest('.card__actions') ||
          event.target.closest('.product-slide-caption')
        ) {
          event.preventDefault();
        }

        // Kick the request off now rather than waiting for <details> to open and
        // the drawer's IntersectionObserver to fire.
        QuickViewCache.warm(this.dataset.productVariantUrl);

        this.quickButton?.classList.add('loading');
        this.closest('body').classList.add('hidden', 'quick-view-open');
  
        const drawer = document.querySelector('quick-view-drawer');
        if (drawer) {
          drawer
            .querySelector('quick-view')
            .setAttribute('data-product-url', `${this.dataset.productVariantUrl}`);
  
          drawer.querySelector('summary').click();
  
          document.dispatchEvent(
            new CustomEvent('quickview:open', {
              detail: {
                productUrl: this.dataset.productUrl
              }
            })
          );
        } else if (this.dataset.productUrl) {
          window.location = this.dataset.productUrl;
        }
      };
  
      this.addEventListener('click', this._onClick);

      if (this.cardExtras) {
        this._onFocus = () => {
          this.cardExtras.style.bottom = '8px';
        };
        this._onBlur = () => {
          this.cardExtras.style.bottom = '-54px';
        };
        this._onQuickViewOpen = () => {
          this.cardExtras.style.bottom = '-54px';
        };
  
        this.quickButton.addEventListener('focus', this._onFocus);
        this.quickButton.addEventListener('blur', this._onBlur);
        this.quickButton.addEventListener('quickview:open', this._onQuickViewOpen);
      }

      this._onQuickviewLoaded = () => {
        this.quickButton?.classList.remove('loading');
      };
  
      this._onQuickviewError = () => {
        this.quickButton?.classList.remove('loading');
      };
  
      document.addEventListener('quickview:loaded', this._onQuickviewLoaded);
      document.addEventListener('quickview:error', this._onQuickviewError);
    }

    disconnectedCallback() {
      if (this._onClick) {
        this.removeEventListener('click', this._onClick);
      }

      if (this._onWarmHover) {
        this.removeEventListener('pointerover', this._onWarmHover);
        this.removeEventListener('pointerout', this._onWarmCancel);
        this.removeEventListener('pointerdown', this._onWarmIntent);
        this.removeEventListener('focusin', this._onWarmIntent);
      }

      if (this.cardExtras) {
        if (this._onFocus) {
          this.quickButton.removeEventListener('focus', this._onFocus);
        }
        if (this._onBlur) {
          this.quickButton.removeEventListener('blur', this._onBlur);
        }
        if (this._onQuickViewOpen) {
          this.quickButton.removeEventListener('quickview:open', this._onQuickViewOpen);
        }
      }
  
      if (this._onQuickviewLoaded) {
        document.removeEventListener('quickview:loaded', this._onQuickviewLoaded);
      }
      if (this._onQuickviewError) {
        document.removeEventListener('quickview:error', this._onQuickviewError);
      }
    }
  }
  customElements.define('quick-view-button', QuickViewButton);
  
  class QuickView extends HTMLElement {
    constructor() {
      super();
      this.overlay = document.querySelector('body > .overlay')
      this.quickModal = this.querySelector('.popup-wrapper')
      this.closeButton = this.querySelector('.button-close')
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.popup-wrapper') || event.target.closest('.button-close')) this.closeQuickView()
      })
      document.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE' && this.querySelector('.popup-wrapper.open')) this.closeQuickView()
      })
    }
  
    connectedCallback() {
      new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
    }
  
    handleIntersection(entries, _observer) {
      if (!entries[0].isIntersecting) return;
      const selector = '.quick-view__content';
      const drawerContent = this.querySelector(selector);
      this.productUrl = this.dataset.productUrl
      this.sectionUrl = QuickViewCache.sectionUrl(this.productUrl);

      // Usually already resolved by the time the click lands — see QuickViewCache.
      QuickViewCache.fetch(this.productUrl)
        .then(contentHTML => {
          setTimeout(() => {
            this.setInnerHTML(drawerContent, contentHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
          }, 1);

          document.body.classList.add('quick-view-load')

          setTimeout(() => {
            drawerContent.classList.add('hide-cover');
  
            document.dispatchEvent(new CustomEvent('quickview:loaded', {
              detail: {
                productUrl: this.dataset.productUrl
              }
            }));
          }, 1);
        })
        .catch(e => {
          console.error(e);

          document.dispatchEvent(new CustomEvent('quickview:error', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        });
    }
  
    setInnerHTML(element, html) {
      element.innerHTML = html;
  
      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        this.overlay.classList.add('open')
      });
    }

    closeQuickView() {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');
      this.closest('body').classList.remove('hidden', 'quick-view-open', 'quick-view-load')
      document.dispatchEvent(new CustomEvent('body:visible'));
      document.dispatchEvent(new CustomEvent('quickview:close'));
      this.closest('details').removeAttribute('open')
      this.overlay.classList.remove('open')
    }
  }
  customElements.define('quick-view', QuickView);
  